// React is used in JSX transformation
import {
  ArrowPathIcon,
  DocumentIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  FolderIcon,
  MagnifyingGlassIcon,
  TrashIcon,
  ChatBubbleLeftIcon
} from '@heroicons/react/24/outline';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Document, Page } from 'react-pdf';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import CustomerSearch from '../components/customers/CustomerSearch';
import Container from '../components/layout/Container';
import DashboardLayout from '../components/layout/DashboardLayout';
import AttachmentPreview from '../components/tickets/AttachmentPreview';
import TicketCommentForm from '../components/tickets/TicketCommentForm';
import TicketCommentList from '../components/tickets/TicketCommentList';
import TicketDates from '../components/tickets/TicketDates';
import TicketHeader from '../components/tickets/TicketHeader';
import TicketHistory from '../components/tickets/TicketHistory';
import TicketPriorityBadge from '../components/tickets/TicketPriorityBadge';
import TicketStatusBadge from '../components/tickets/TicketStatusBadge';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/custom/Dialog';
import FileUpload from '../components/ui/custom/FileUpload';
import { TICKET_PRIORITIES, TICKET_STATUSES } from '../constants/ticketConstants';
import { useAuthContext } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useTicketRefresh } from '../contexts/TicketRefreshContext';
import { useMetadata } from '../hooks/useMetadata';
import { AuditLog, auditService } from '../services/auditService';
import { CommentRequest, commentService } from '../services/commentService';
import { customerService } from '../services/customerService';
import { FormTemplate, formTemplateService } from '../services/formTemplateService';
import { groupAgentsService } from '../services/groupAgentsService';
import { httpClient } from '../services/httpClient';
import omnichannelService, { Conversation } from '../services/omnichannelService';
import { tagService } from '../services/tagService';
import { ticketService } from '../services/ticketService';
import { uploadService } from '../services/uploadService';
import { userService } from '../services/userService';
import { Priority, Status } from '../types';
import { Comment } from '../types/comment';
import { CreateTicketFormData, Ticket } from '../types/ticket';
import { formatFileSize } from '../utils/formatters';
import { formatProfilePictureUrl } from '../utils/imageUtils';

import toast from 'react-hot-toast';
import AsyncSelect from 'react-select/async';

// Component to render PDF thumbnails for new file attachments
function NewFilePdfThumbnail({ file, theme }: { file: File; theme: string }) {
  const [thumbnail, setThumbnail] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [objectUrl, setObjectUrl] = useState<string>('');

  useEffect(() => {
    // Create object URL for the PDF file
    const url = URL.createObjectURL(file);
    setObjectUrl(url);

    // Clean up object URL when component unmounts
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [file]);

  return (
    <div
      className={`relative w-full h-full flex items-center justify-center ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'}`}
    >
      {!error ? (
        <>
          <Document
            file={objectUrl}
            onLoadSuccess={() => setThumbnail(true)}
            onLoadError={() => setError(true)}
            loading={null}
            className="pdf-thumbnail"
          >
            <Page
              pageNumber={1}
              width={120}
              height={120}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              className={thumbnail ? '' : 'hidden'}
              scale={1.0}
            />
          </Document>
          {!thumbnail && !error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="w-5 h-5 border-t-2 border-red-400 rounded-full animate-spin mb-2"></div>
              <span className="text-xs text-gray-500 dark:text-gray-400">Loading PDF</span>
            </div>
          )}
        </>
      ) : (
        <DocumentTextIcon className="h-12 w-12 text-red-400" />
      )}
      <div className="absolute bottom-0 right-0 bg-red-500 text-white text-[10px] px-1 rounded-tl-sm">
        PDF
      </div>
    </div>
  );
}

function TicketDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const { user } = useAuthContext();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const { refreshTrigger } = useTicketRefresh();
  const [comments, setComments] = useState<Comment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingComments, setIsLoadingComments] = useState(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [isLoadingAuditLogs, setIsLoadingAuditLogs] = useState(false);

  const [beforeEditTicket, setBeforeEditTicket] = useState<Ticket | null>(null);
  const [linkedConversation, setLinkedConversation] = useState<Conversation | null>(null);

  // Get the source route from location state
  const sourceRoute = location.state?.from || '/tickets';

  const handleBackToList = () => {
    // Use the sourceRoute variable defined above
    const filterState = location.state?.filterState || {};

    // Determine the route and prepare state based on source
    if (sourceRoute === '/reports') {
      navigate('/reports', {
        state: {
          filterState: {
            startDate: filterState.startDate,
            endDate: filterState.endDate,
            currentPage: filterState.currentPage
          }
        }
      });
    } else if (sourceRoute === '/dashboard') {
      navigate('/dashboard', {
        state: {
          fromTicket: true,
          dateRange: {
            startDate: filterState.startDate,
            endDate: filterState.endDate
          }
        }
      });
    } else {
      navigate('/tickets', {
        state: {
          filterState: {
            selectedStatus: filterState.selectedStatus || '',
            searchQuery: filterState.searchQuery || '',
            sortBy: filterState.sortBy || 'createdAt',
            sortOrder: filterState.sortOrder || 'desc',
            currentPage: filterState.currentPage || 1
          }
        }
      });
    }
  };

  const [editedTitle, setEditedTitle] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [editedStatus, setEditedStatus] = useState<Status>('new');
  const [editedPriority, setEditedPriority] = useState<Priority>('medium');
  const [editedIsPublic, setEditedIsPublic] = useState(true);
  const [editedGroup, setEditedGroup] = useState<string>('');
  const [editedAssignee, setEditedAssignee] = useState<string>('');
  const [editedType, setEditedType] = useState<string>('');
  const [editedCategory, setEditedCategory] = useState<string>('');
  const [editedSource, setEditedSource] = useState<string>('');
  const [editedTags, setEditedTags] = useState<Array<{ label: string; value: string }>>([]);
  const [groupAgents, setGroupAgents] = useState<Array<{ value: string; label: string }>>([]);
  const [editError, setEditError] = useState('');
  const [editedCustomer, setEditedCustomer] = useState<{
    id: number;
    full_name: string;
    phone?: string;
  } | null>(null);
  const [editedPhoneNumber, setEditedPhoneNumber] = useState<string>('');
  const [searchPhoneNumber, setSearchPhoneNumber] = useState('');
  const [editedFormTemplateId, setEditedFormTemplateId] = useState<number | null>(null);
  const [editedFormResponse, setEditedFormResponse] = useState<any | null>(null);
  // Using underscore prefix to indicate this is used indirectly
  const [_formTemplate, setFormTemplate] = useState<FormTemplate | null>(null);
  // Form field labels for dynamic form rendering
  const [, setFormFieldLabels] = useState<Record<string, string>>({});
  // These variables are currently not used but kept for future feature implementation
  // const [isSearchingCustomer, setIsSearchingCustomer] = useState<boolean>(false);
  // const [customerError, setCustomerError] = useState<string>('');
  const setIsSearchingCustomer = useState<boolean>(false)[1];
  const setCustomerError = useState<string>('')[1];
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState('');
  const [isCreatingCustomer, setIsCreatingCustomer] = useState(false);
  const [externalRefConfig, setExternalRefConfig] = useState<any>(null);
  const [isLoadingExternalRef, setIsLoadingExternalRef] = useState(false);
  const [externalRef, setExternalRef] = useState('');
  const [searchResults, setSearchResults] = useState<
    Array<{
      id: number;
      full_name: string;
      phone?: string;
    }>
  >([]);
  const [editedAttachments, setEditedAttachments] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});

  const customerNameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ticket) {
      // Fetch customer details if we have a customer_id
      if (ticket.customer_id) {
        customerService
          .getCustomer(ticket.customer_id)
          .then((customer) => {
            setEditedCustomer({
              id: customer.id,
              full_name: customer.full_name,
              phone: customer.phone
            });
            setEditedPhoneNumber(customer.phone || '');
            setSearchPhoneNumber(customer.phone || '');
          })
          .catch((error) => {
            console.error('Error fetching customer:', error);
          });
      }
    }
  }, [ticket, isEditing]);

  // Load external reference configuration
  useEffect(() => {
    const loadExternalRefConfig = async () => {
      try {
        const response = await httpClient.get('/api/external-reference/config');
        if (response) {
          setExternalRefConfig(response);
        }
      } catch (error) {
        console.error('Error loading external reference config:', error);
      }
    };

    loadExternalRefConfig();
  }, []);

  const canDelete =
    user?.role === 'admin' ||
    (user?.role === 'agent' && ticket?.created_by?.id === user?.id) ||
    (user?.role === 'requester' && ticket?.created_by?.id === user?.id);
  const canEdit =
    user?.role === 'admin' ||
    (user?.role === 'agent' && ticket?.created_by?.id === user?.id) ||
    (user?.role === 'requester' && ticket?.created_by?.id === user?.id);

  // Store the source route and page in localStorage
  useEffect(() => {
    if (location.state?.from) {
      localStorage.setItem('ticketSourceRoute', location.state.from);
    }
    if (location.state?.page) {
      localStorage.setItem('ticketSourcePage', location.state.page);
    }
  }, [location.state]);

  // Function to fetch ticket data and comments
  const fetchTicketData = async () => {
    if (!id) return;

    try {
      const fetchedTicket = await ticketService.getTicket(Number(id));
      if (!isEditing) {
        setTicket(fetchedTicket);
      }

      // If ticket has form_template_id, fetch the form template to get field labels
      if (fetchedTicket.form_template_id) {
        try {
          const template = await formTemplateService.getFormTemplate(
            fetchedTicket.form_template_id
          );

          setFormTemplate(template);

          // Extract field labels from form template schema
          const labels: Record<string, string> = {};
          if (template && template.schema && template.schema.fields) {
            template.schema.fields.forEach((field: { name: string; label?: string }) => {
              if (field && typeof field === 'object' && 'name' in field && 'label' in field) {
                labels[field.name] = field.label || field.name;
              } else {
                console.warn('fetchTicketData: Invalid field structure:', field);
              }
            });
          } else {
            console.warn(
              'fetchTicketData: Template schema structure is not as expected:',
              template
            );
          }

          // If we have form response data but no labels, create default labels from keys
          if (fetchedTicket.form_response && Object.keys(labels).length === 0) {
            Object.keys(fetchedTicket.form_response).forEach((key) => {
              // Convert snake_case or camelCase to Title Case
              const label = key
                .replace(/_/g, ' ')
                .replace(/([A-Z])/g, ' $1')
                .replace(/^./, (str) => str.toUpperCase())
                .trim();
              labels[key] = label;
            });
          }

          setFormFieldLabels(labels);

          // Initialize edited form data if in edit mode
          if (isEditing) {
            setEditedFormTemplateId(fetchedTicket.form_template_id);
            setEditedFormResponse(fetchedTicket.form_response || {});
          }
        } catch (formError) {
          console.error('Error fetching form template:', formError);
        }
      }
    } catch (error) {
      console.error('Error fetching ticket:', error);
    }

    try {
      const fetchedComments = await commentService.getTicketComments(id);
      // Sort comments by date in descending order (newest first)
      const sortedComments = fetchedComments.sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setComments(sortedComments);
    } catch (error) {
      console.error('Error fetching comments:', error);
      toast.error('Failed to load comments');
    } finally {
      setIsLoading(false);
      setIsLoadingComments(false);
    }

    // Fetch audit logs for the ticket
    fetchAuditLogs();
  };

  // Function to fetch audit logs for the ticket
  const fetchAuditLogs = async () => {
    if (!id) return;

    setIsLoadingAuditLogs(true);
    try {
      const response = await auditService.getAuditLogs({
        entityType: 'Ticket', // Capitalize first letter to match backend expectation
        entityId: parseInt(id),
        limit: 50,
        page: 1
      });
      setAuditLogs(response.logs);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      toast.error('Failed to load ticket history');
    } finally {
      setIsLoadingAuditLogs(false);
    }
  };

  // Initial data fetch when component mounts or id changes
  useEffect(() => {
    fetchTicketData();
  }, [id]);

  // Fetch linked conversation
  useEffect(() => {
    if (ticket?.id) {
      omnichannelService
        .getConversationByTicketId(ticket.id)
        .then((conv) => setLinkedConversation(conv))
        .catch(() => setLinkedConversation(null));
    }
  }, [ticket?.id]);

  // Refresh data when notification is received (refreshTrigger changes)
  useEffect(() => {
    if (refreshTrigger > 0 && id) {
      fetchTicketData();
    }
  }, [refreshTrigger, id]);

  // const isAgent = user?.role === 'agent' || user?.role === 'admin';

  const handleDelete = async () => {
    if (!ticket?.id) {
      console.error('Cannot delete ticket: Invalid ticket ID');
      return;
    }

    try {
      await ticketService.deleteTicket(ticket.id);
      setIsDeleteModalOpen(false);
      handleBackToList();
    } catch (error) {
      console.error('Error deleting ticket:', error);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!ticket?.id) {
      console.error('Cannot update ticket status: Invalid ticket ID');
      return;
    }

    try {
      await ticketService.updateStatus(ticket.id, newStatus, user?.id || 0);

      // Fetch updated ticket data
      const updatedTicket = await ticketService.getTicket(ticket.id);
      setTicket(updatedTicket);
    } catch (error) {
      console.error('Error updating ticket status:', error);
    }
  };

  const handleAddComment = async (
    data: { content: string; attachments?: File[] },
    onUploadProgress: (progress: number) => void
  ) => {
    if (user) {
      try {
        // First add the comment to the server
        const comment = await commentService.addComment(id, { content: data.content });
        // check if attachments are uploaded
        if (data.attachments && data.attachments.length > 0) {
          try {
            await uploadService.uploadCommentFiles(
              Number(id),
              data.attachments,
              Number(comment.id),
              (progress) => onUploadProgress?.(progress.progress)
            );
          } catch (error) {
            console.error('Error uploading comment files:', error);
            toast.error('Failed to upload comment files');
          }
        }

        // Fetch the updated comments list from server
        const fetchedComments = await commentService.getTicketComments(id);
        // Sort comments by date in descending order (newest first)
        const sortedComments = fetchedComments.sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        setComments(sortedComments);
        toast.success('Comment added successfully');
      } catch (error) {
        console.error('Error adding comment:', error);
        toast.error('Failed to add comment');
      }
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    try {
      await commentService.deleteComment(commentId);

      // Fetch the updated comments list from server
      const fetchedComments = await commentService.getTicketComments(id);
      // Sort comments by date in descending order (newest first)
      const sortedComments = fetchedComments.sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setComments(sortedComments);
      toast.success('Comment deleted successfully');
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast.error('Failed to delete comment');
    }
  };

  const handleEditComment = async (
    commentId: number,
    content: string,
    attachments: File[] = [],
    onProgress?: (progress: { loaded: number; total: number }, fileName: string) => void
  ) => {
    try {
      // Update the comment content on the server
      const updateData: CommentRequest = { content };
      await commentService.updateComment(commentId, updateData);

      // Upload any new attachments if they exist
      if (attachments && attachments.length > 0) {
        // Create a wrapper for the progress callback to match the upload service's expected format
        const progressWrapper = onProgress
          ? (progress: { fileName: string; progress: number }) => {
              // The upload service provides overall progress for all files
              // We'll distribute this progress equally across all files
              attachments.forEach((file) => {
                onProgress(
                  {
                    loaded: (progress.progress * file.size) / 100,
                    total: file.size
                  },
                  file.name
                );
              });
            }
          : undefined;

        await uploadService.uploadCommentFiles(Number(id), attachments, commentId, progressWrapper);
      }

      // Fetch the updated comments list from server
      const fetchedComments = await commentService.getTicketComments(id);
      // Sort comments by date in descending order (newest first)
      const sortedComments = fetchedComments.sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setComments(sortedComments);

      toast.success('Comment updated successfully');
    } catch (error) {
      console.error('Error updating comment:', error);
      toast.error('Failed to update comment');
      throw error; // Propagate error to the component
    }
  };

  // Removed handleCancelCommentEdit as we're now using inline editing

  const handleSaveEdit = async () => {
    if (!user?.id) return;

    // Maximum number of files allowed (from server config)
    const MAX_FILES = 5;

    // Check if adding these files would exceed the limit
    if (editedAttachments.length > MAX_FILES) {
      setEditError(
        `You can only upload a maximum of ${MAX_FILES} files. Please remove some attachments.`
      );
      return;
    }

    setIsSaving(true);
    setEditError('');

    try {
      // Get the latest ticket data from the server
      const latestTicket = await ticketService.getTicket(id);

      // Compare with our before edit version
      if (
        beforeEditTicket &&
        new Date(beforeEditTicket.updated_at) < new Date(latestTicket.updated_at)
      ) {
        setEditError(t('pages.ticketDetail.ticketUpdatedByAnotherUser'));
        return;
      }

      const updatedTicket: Partial<CreateTicketFormData> = {
        title: editedTitle,
        description: editedDescription,
        status: editedStatus,
        priority: editedPriority,
        assignedTo: editedAssignee || undefined,
        group: editedGroup || undefined,
        isPublic: editedIsPublic,
        type: editedType || undefined,
        category: editedCategory || undefined,
        source: editedSource || undefined,
        tags: editedTags.map((tag) => tag.value),
        customerId: editedCustomer?.id,
        customerName: editedCustomer?.full_name || '',
        phoneNumber: editedPhoneNumber,
        updatedById: user?.id,
        formTemplateId: editedFormTemplateId || undefined,
        formResponse: editedFormResponse // Using camelCase for consistency
      };

      // First upload attachments if any
      if (editedAttachments.length > 0) {
        try {
          await uploadService.uploadFiles(parseInt(id), editedAttachments, (progress) => {
            setUploadProgress((prev) => ({
              ...prev,
              [progress.fileName]: progress.progress
            }));
          });
        } catch (error) {
          console.error('Error uploading attachments:', error);
          setEditError(t('pages.ticketDetail.uploadAttachmentsError'));
          throw new Error('Failed to upload attachments');
        }
      }

      // Update ticket
      await ticketService.updateTicket(id, updatedTicket, user?.id || 0);

      // Get updated ticket data including new attachments
      const updatedTicketData = await ticketService.getTicket(id);
      setTicket(updatedTicketData);

      // Reset edit state
      setIsEditing(false);
      setEditedAttachments([]);
      setUploadProgress({});
      toast.success(t('pages.ticketDetail.ticketUpdated'));
    } catch (error) {
      console.error('Error updating ticket:', error);
      toast.error(t('pages.ticketDetail.updateError'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId: number) => {
    try {
      await uploadService.deleteAttachment(attachmentId);
      // Refresh the ticket to get updated attachments
      const updatedTicket = await ticketService.getTicket(id);
      setTicket(updatedTicket);
      toast.success(t('pages.ticketDetail.attachmentDeleted'));
    } catch (error) {
      console.error('Error deleting attachment:', error);
      toast.error(t('pages.ticketDetail.deleteAttachmentError'));
    }
  };

  const handleDeleteCommentAttachment = async (attachmentId: number) => {
    try {
      await uploadService.deleteAttachment(attachmentId);
      // Refresh the comments to get updated attachments
      const fetchedComments = await commentService.getTicketComments(id);
      // Sort comments by date in descending order (newest first)
      const sortedComments = fetchedComments.sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setComments(sortedComments);
      toast.success(t('pages.ticketDetail.attachmentDeleted'));
    } catch (error) {
      console.error('Error deleting attachment:', error);
      toast.error(t('pages.ticketDetail.deleteAttachmentError'));
    }
  };

  const { groups, categories, types, sources } = useMetadata();

  const userIsInGroup = useMemo(() => {
    if (!groupAgents?.length || !user?.id) return false;
    return !!groupAgents.find((agent) => agent.value === user.id.toString());
  }, [groupAgents, user]);

  // Load agents when group changes
  useEffect(() => {
    const loadGroupAgents = async () => {
      const ticketGroupId = ticket?.group_id;
      if (!editedGroup && !ticketGroupId) {
        setGroupAgents([]);
        return;
      }

      try {
        const groupId = editedGroup ? parseInt(editedGroup) : ticketGroupId;
        if (groupId) {
          const agents = await groupAgentsService.getAgentsByGroupId(groupId);
          const agentDetails = await Promise.all(
            agents.map(async (agent) => {
              const user = await userService.getUser(agent.user_id);
              return {
                value: user.id.toString(),
                label: `${user.full_name} (${user.email})`
              };
            })
          );
          setGroupAgents(agentDetails);

          // If current assignee is not in the new group's agents list, reset the assignee
          if (editedAssignee && !agentDetails.some((agent) => agent.value === editedAssignee)) {
            setEditedAssignee('');
          }
        } else {
          setGroupAgents([]);
        }
      } catch (error) {
        console.error('Error loading group agents:', error);
        setGroupAgents([]);
      }
    };

    loadGroupAgents();
  }, [editedGroup, ticket?.group_id, editedAssignee]);

  useEffect(() => {
    if (ticket && isEditing && groups) {
      // Initialize edited tags when entering edit mode
      const initialTags = ticket.tags.map((tag) => ({
        label: tag.name,
        value: tag.id.toString()
      }));
      setEditedTags(initialTags);

      // Find the matching metadata items
      const matchingGroup = groups.find((group) => group.label === ticket.group_name);
      const matchingType = types?.find((type) => type.label === ticket.type_name);
      const matchingCategory = categories?.find((cat) => cat.label === ticket.category_name);
      const matchingSource = sources?.find((src) => src.label === ticket.source_name);

      // Set the values if matches are found
      if (matchingGroup) setEditedGroup(matchingGroup.value);
      if (matchingType) setEditedType(matchingType.value);
      if (matchingCategory) setEditedCategory(matchingCategory.value);
      if (matchingSource) setEditedSource(matchingSource.value);

      // Set other edit fields
      setEditedTitle(ticket.title);
      setEditedDescription(ticket.description);
      setEditedStatus(ticket.status as Status);
      setEditedPriority(ticket.priority as Priority);
      setEditedIsPublic(ticket.is_public);
      setEditedAssignee(ticket.assigned_to?.id ? ticket.assigned_to.id.toString() : '');
      setSearchPhoneNumber(ticket.customer?.phone || '');

      // Set form_id and form_response when editing
      setEditedFormTemplateId(ticket.form_template_id || null);
      // Make a deep copy of the form response to ensure we're not modifying the original object
      const formResponseCopy = ticket.form_response
        ? JSON.parse(JSON.stringify(ticket.form_response))
        : {};
      setEditedFormResponse(formResponseCopy);

      // Fetch form template to get field labels when entering edit mode
      if (ticket.form_template_id) {
        formTemplateService
          .getFormTemplate(ticket.form_template_id)
          .then((template) => {
            setFormTemplate(template);

            // Extract field labels from form template schema
            const labels: Record<string, string> = {};
            if (template && template.schema && template.schema.fields) {
              template.schema.fields.forEach((field: any) => {
                labels[field.name] = field.label || field.name;
              });
            } else {
              console.warn('Template schema structure is not as expected:', template);
            }
            setFormFieldLabels(labels);
          })
          .catch((error) => {
            console.error('Error fetching form template for edit mode:', error);
          });
      }
    }
  }, [ticket, isEditing, groups, types, categories, sources]);

  const loadInitialTags = async () => {
    try {
      const tags = await tagService.getAllTags();
      const options = tags
        .filter((tag) => tag.is_active)
        .map((tag) => ({
          label: tag.name,
          value: tag.id.toString()
        }));
      return options;
    } catch (error) {
      console.error('Error loading initial tags:', error);
      return [];
    }
  };

  const debouncedLoadTags = async (
    inputValue: string,
    callback: (options: { label: string; value: string }[]) => void
  ) => {
    if (!inputValue || inputValue.length < 1) {
      callback([]);
      return;
    }

    try {
      const tags = await tagService.searchTags(inputValue);
      const filteredTags = tags.filter(
        (tag) =>
          !editedTags.some((selected) => selected.value === tag.id.toString()) &&
          tag.name.toLowerCase().includes(inputValue.toLowerCase())
      );

      const options = filteredTags.map((tag) => ({
        label: tag.name,
        value: tag.id.toString()
      }));

      callback(options);
    } catch (error) {
      console.error('Error fetching tags:', error);
      callback([]);
    }
  };

  const loadTagOptions = (
    inputValue: string,
    callback: (options: { label: string; value: string }[]) => void
  ) => {
    if (!inputValue) {
      loadInitialTags().then(callback);
      return;
    }

    if (loadTagOptions.timeout) {
      clearTimeout(loadTagOptions.timeout);
    }

    loadTagOptions.timeout = setTimeout(() => {
      debouncedLoadTags(inputValue, callback);
    }, 300);
  };
  loadTagOptions.timeout = null as any;

  const handleTagChange = (selectedOptions: any) => {
    setEditedTags(selectedOptions || []);
  };

  // External reference lookup function
  const handleExternalReferenceSearch = async () => {
    if (!externalRefConfig?.enabled || !searchPhoneNumber) return;

    setIsLoadingExternalRef(true);
    try {
      const response = await httpClient.post('/api/external-reference/lookup', {
        phone: searchPhoneNumber
      });

      const data = (response as any).data;

      // Check if customer data was found
      if (data && (data.name || data._id)) {
        // Auto-fill customer name and external reference ID
        if (data.name) {
          setNewCustomerName(data.name);
        }
        if (data._id) {
          setExternalRef(data._id);
        }
        toast.success(t('integrationSettings.externalReference.lookupSuccess'));
      } else {
        // No customer information found
        toast.error(t('integrationSettings.externalReference.noCustomerFound'));
      }
    } catch (error: any) {
      console.error('External reference lookup error:', error);
      if (error.response?.status === 408) {
        toast.error(t('integrationSettings.externalReference.lookupTimeout'));
      } else {
        toast.error(t('integrationSettings.externalReference.lookupError'));
      }
    } finally {
      setIsLoadingExternalRef(false);
    }
  };

  const handleCreateCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCustomerName || !searchPhoneNumber) return;

    setIsCreatingCustomer(true);
    try {
      const newCustomer = await customerService.createCustomer({
        full_name: newCustomerName,
        phone: searchPhoneNumber,
        external_ref: externalRef || undefined
      });

      setEditedCustomer({ id: newCustomer.id, full_name: newCustomer.full_name });
      setEditedPhoneNumber(searchPhoneNumber);
      setNewCustomerName('');
      setExternalRef('');
      setIsCustomerModalOpen(false);
    } catch (error) {
      console.error('Error creating customer:', error);
    } finally {
      setIsCreatingCustomer(false);
    }
  };

  const handlePickupTicket = async () => {
    if (user?.id && userIsInGroup) {
      try {
        setIsSaving(true);
        await ticketService.updateTicket(
          id,
          {
            assignedTo: user.id.toString()
          },
          user.id || 0
        );
        // refresh ticket
        const fetchedTicket = await ticketService.getTicket(Number(id));
        setTicket(fetchedTicket);
      } catch (error) {
        console.error('Error picking up ticket:', error);
        toast.error(t('pages.ticketDetail.updateError'));
      } finally {
        setIsSaving(false);
      }
    }
  };

  if (!id) {
    return <Navigate to="/tickets" />;
  }

  if (isLoading) {
    return (
      <DashboardLayout>
        <Container>
          <div
            className={`flex items-center justify-center py-8 ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}
          >
            <div className="animate-pulse">Loading ticket...</div>
          </div>
        </Container>
      </DashboardLayout>
    );
  }

  if (!ticket && isLoading === false) {
    return (
      <DashboardLayout>
        <Container>
          <div
            className={`mt-4 px-4 py-3 rounded-lg ${
              theme === 'dark'
                ? 'bg-red-900/30 border-red-800/30 text-red-400'
                : 'bg-red-50 border-red-200 text-red-600'
            } border`}
          >
            Ticket not found
          </div>
        </Container>
      </DashboardLayout>
    );
  }

  if (!ticket?.id) {
    console.error('Cannot load ticket: Invalid ticket ID');
    return null;
  }

  return (
    <DashboardLayout>
      <Container className="py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel - Ticket Details */}
          <div className="lg:col-span-2">
            <div
              className={`${
                theme === 'dark' ? 'bg-gray-800' : 'bg-white'
              } shadow-sm rounded-xl overflow-hidden border border-border`}
            >
              {/* Header */}
              <div
                className={`px-6 py-4 ${
                  theme === 'dark'
                    ? 'bg-gradient-to-r from-blue-900/30 to-indigo-900/30'
                    : 'bg-gradient-to-r from-blue-50 to-indigo-50'
                }`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    {isEditing ? (
                      <div className="flex flex-col space-y-4">
                        <div className="flex items-center space-x-2">
                          <span className="text-lg font-semibold">
                            {t('pages.ticketDetail.editTicket')} #{ticket.id}
                          </span>
                        </div>

                        <input
                          type="text"
                          value={editedTitle}
                          onChange={(e) => setEditedTitle(e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                            theme === 'dark'
                              ? 'bg-gray-800 border-gray-700 text-white focus:border-blue-500'
                              : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                          }`}
                        />
                      </div>
                    ) : (
                      <>
                        {linkedConversation && (
                          <div className="mb-4">
                            <button
                              onClick={() =>
                                navigate('/atrium', {
                                  state: { selectedConversationId: linkedConversation.id }
                                })
                              }
                              className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                            >
                              <ChatBubbleLeftIcon className="h-4 w-4" />
                              View Linked Conversation
                            </button>
                          </div>
                        )}
                        <TicketHeader ticket={ticket} onStatusChange={handleStatusChange} />
                      </>
                    )}
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap gap-3">
                  {isEditing ? (
                    <>
                      <select
                        value={editedStatus}
                        onChange={(e) => setEditedStatus(e.target.value as Status)}
                        className={`px-2 py-1 text-xs font-medium border rounded-full ${
                          theme === 'dark'
                            ? editedStatus === 'new'
                              ? '[&>option]:bg-gray-800 [&>option]:text-green-200 bg-green-900/30 border-green-500/50 text-green-400 hover:bg-green-900/40 focus:border-green-400'
                              : editedStatus === 'processing'
                                ? '[&>option]:bg-gray-800 [&>option]:text-yellow-200 bg-yellow-900/30 border-yellow-500/50 text-yellow-400 hover:bg-yellow-900/40 focus:border-yellow-400'
                                : editedStatus === 'resolved'
                                  ? '[&>option]:bg-gray-800 [&>option]:text-blue-200 bg-blue-900/30 border-blue-500/50 text-blue-400 hover:bg-blue-900/40 focus:border-blue-400'
                                  : '[&>option]:bg-gray-800 [&>option]:text-gray-200 bg-gray-800/30 border-gray-500/50 text-gray-400 hover:bg-gray-800/40 focus:border-gray-400'
                            : editedStatus === 'new'
                              ? 'bg-green-100 text-green-800 border-green-200'
                              : editedStatus === 'processing'
                                ? 'bg-yellow-100 text-yellow-800 border-yellow-200'
                                : editedStatus === 'resolved'
                                  ? 'bg-blue-100 text-blue-800 border-blue-200'
                                  : 'bg-gray-100 text-gray-800 border-gray-200'
                        }`}
                      >
                        <option
                          value=""
                          className={theme === 'dark' ? 'bg-gray-800 text-gray-400' : ''}
                        >
                          Select Status
                        </option>
                        {TICKET_STATUSES.map((status) => (
                          <option
                            key={status.value}
                            value={status.value}
                            className={theme === 'dark' ? 'bg-gray-800' : ''}
                          >
                            {status.label}
                          </option>
                        ))}
                      </select>
                      <select
                        value={editedPriority}
                        onChange={(e) => setEditedPriority(e.target.value as Priority)}
                        className={`px-2 py-1 text-xs font-medium border rounded-full ${
                          theme === 'dark'
                            ? editedPriority === 'low'
                              ? '[&>option]:bg-gray-800 [&>option]:text-gray-200 bg-gray-800/30 border-gray-500/50 text-gray-400 hover:bg-gray-800/40 focus:border-gray-400'
                              : editedPriority === 'medium'
                                ? '[&>option]:bg-gray-800 [&>option]:text-blue-200 bg-blue-900/30 border-blue-500/50 text-blue-400 hover:bg-blue-900/40 focus:border-blue-400'
                                : editedPriority === 'high'
                                  ? '[&>option]:bg-gray-800 [&>option]:text-orange-200 bg-orange-900/30 border-orange-500/50 text-orange-400 hover:bg-orange-900/40 focus:border-orange-400'
                                  : '[&>option]:bg-gray-800 [&>option]:text-red-200 bg-red-900/30 border-red-500/50 text-red-400 hover:bg-red-900/40 focus:border-red-400'
                            : editedPriority === 'low'
                              ? 'bg-gray-100 text-gray-800 border-gray-200'
                              : editedPriority === 'medium'
                                ? 'bg-blue-100 text-blue-800 border-blue-200'
                                : editedPriority === 'high'
                                  ? 'bg-orange-100 text-orange-800 border-orange-200'
                                  : 'bg-red-100 text-red-800 border-red-200'
                        }`}
                      >
                        <option
                          value=""
                          className={theme === 'dark' ? 'bg-gray-800 text-gray-400' : ''}
                        >
                          Select Priority
                        </option>
                        {TICKET_PRIORITIES.map((priority) => (
                          <option
                            key={priority.value}
                            value={priority.value}
                            className={theme === 'dark' ? 'bg-gray-800' : ''}
                          >
                            {priority.label}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={() => setEditedIsPublic(!editedIsPublic)}
                        className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
                          theme === 'dark'
                            ? editedIsPublic
                              ? 'bg-green-900/30 text-green-400 hover:bg-green-900/40 border border-green-500/50'
                              : 'bg-yellow-900/30 text-yellow-400 hover:bg-yellow-900/40 border border-yellow-500/50'
                            : editedIsPublic
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {editedIsPublic ? 'Public' : 'Private'}
                      </button>
                    </>
                  ) : (
                    <>
                      <TicketStatusBadge status={ticket.status as Status} />
                      <TicketPriorityBadge priority={ticket.priority as Priority} />
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          theme === 'dark'
                            ? ticket.is_public
                              ? 'bg-green-900/30 text-green-400'
                              : 'bg-yellow-900/30 text-yellow-400'
                            : ticket.is_public
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {ticket.is_public ? 'Public' : 'Private'}
                      </span>
                    </>
                  )}

                  <div className="flex space-x-2 ml-auto">
                    {editError && (
                      <div
                        className={`px-3 py-2 rounded-md text-sm ${
                          theme === 'dark' ? 'bg-red-900/30 text-red-400' : 'bg-red-50 text-red-700'
                        }`}
                      >
                        {editError}
                      </div>
                    )}
                    {canEdit && (
                      <button
                        onClick={() => {
                          if (isEditing) {
                            handleSaveEdit();
                          } else {
                            setEditedTitle(ticket.title);
                            setEditedDescription(ticket.description);
                            setEditedStatus(ticket.status as Status);
                            setEditedPriority(ticket.priority as Priority);
                            setEditedIsPublic(ticket.is_public);
                            setIsEditing(true);
                            setEditError('');
                            setBeforeEditTicket(ticket);
                          }
                        }}
                        disabled={isSaving}
                        className={`inline-flex items-center px-3 py-2 border rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                          theme === 'dark'
                            ? isEditing
                              ? 'border-green-500/50 bg-green-900/20 text-green-400 hover:bg-green-900/30 focus:border-green-400'
                              : 'border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600 focus:ring-offset-gray-800 focus:ring-indigo-500'
                            : isEditing
                              ? 'border-green-300 bg-green-50 text-green-700 hover:bg-green-100 focus:ring-green-500'
                              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:ring-indigo-500'
                        } ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {isSaving
                          ? 'Saving...'
                          : isEditing
                            ? t('pages.ticketDetail.saveChanges')
                            : t('pages.ticketDetail.editTicket')}
                      </button>
                    )}
                    {isEditing && (
                      <button
                        onClick={async () => {
                          try {
                            if (editError) {
                              const latestTicket = await ticketService.getTicket(Number(id));
                              setTicket(latestTicket);
                            }
                            setIsEditing(false);
                            setIsSearchingCustomer(false);
                            setCustomerError('');
                            setEditedTitle(ticket.title);
                            setEditedDescription(ticket.description);
                            setEditedStatus(ticket.status as Status);
                            setEditedPriority(ticket.priority as Priority);
                            setEditedIsPublic(ticket.is_public);
                            setEditError('');
                          } catch (error) {
                            console.error(error);
                            setIsEditing(false);
                            setEditError('Failed to load ticket');
                          }
                        }}
                        disabled={isSaving}
                        className={`inline-flex items-center px-3 py-2 border rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                          theme === 'dark'
                            ? 'border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600 focus:ring-offset-gray-800 focus:ring-indigo-500'
                            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:ring-indigo-500'
                        } ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {t('pages.ticketDetail.cancel')}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Ticket Content */}
              <div className="p-6">
                <div className="mt-4">
                  <div className={`prose max-w-none ${theme === 'dark' ? 'prose-invert' : ''}`}>
                    {isEditing ? (
                      <div className="space-y-4">
                        {/* Form fields editing section */}
                        {editedFormTemplateId && (
                          <div className="mb-4 p-4 border rounded-lg">
                            <h4
                              className={`text-md font-medium mb-3 ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}
                            >
                              {_formTemplate?.name || `Template ${editedFormTemplateId}`}
                            </h4>
                            <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                              {_formTemplate?.schema?.fields?.map((field: any) => {
                                const key = field.name;
                                const value = editedFormResponse?.[key] || '';
                                return (
                                  <div key={key} className="mb-3">
                                    <label
                                      className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}
                                    >
                                      {field.label || key}
                                    </label>
                                    <input
                                      type="text"
                                      value={value as string}
                                      onChange={(e) => {
                                        const updatedFormResponse = {
                                          ...editedFormResponse,
                                          [key]: e.target.value
                                        };
                                        setEditedFormResponse(updatedFormResponse);
                                      }}
                                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                                        theme === 'dark'
                                          ? 'bg-gray-800 border-gray-700 text-white focus:border-blue-500'
                                          : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                                      }`}
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Description field */}
                        <textarea
                          value={editedDescription}
                          onChange={(e) => setEditedDescription(e.target.value)}
                          rows={6}
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                            theme === 'dark'
                              ? 'bg-gray-800 border-gray-700 text-white focus:border-blue-500'
                              : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                          }`}
                        />
                      </div>
                    ) : (
                      <div>
                        {/* Display form response if available, otherwise show plain description */}
                        {ticket.form_template_id && ticket.form_response ? (
                          <div>
                            {/* Form response display with labels and values */}
                            <div className="mb-6">
                              <h4
                                className={`text-md font-medium mb-3 ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}
                              >
                                {_formTemplate?.name || `Template ${ticket.form_template_id}`}
                              </h4>
                              <div
                                className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'}`}
                              >
                                <div className="grid grid-cols-2 gap-3">
                                  {_formTemplate?.schema?.fields?.map((field: any) => {
                                    const key = field.name;
                                    const value = ticket.form_response?.[key];
                                    return (
                                      <React.Fragment key={key}>
                                        <div
                                          className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}
                                        >
                                          {field.label || key}:
                                        </div>
                                        <div
                                          className={`${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}
                                        >
                                          {value !== null && value !== undefined
                                            ? String(value)
                                            : '-'}
                                        </div>
                                      </React.Fragment>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>

                            {/* Description */}
                            {ticket.description && (
                              <div className="mt-6">
                                <h4
                                  className={`text-md font-medium mb-3 ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}
                                >
                                  Description
                                </h4>
                                <div className="whitespace-pre-wrap">{ticket.description}</div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="whitespace-pre-wrap">{ticket.description}</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Attachments Section */}
                <div className="mt-6 border-t pt-6">
                  <h3
                    className={`text-lg font-medium mb-4 ${
                      theme === 'dark' ? 'text-gray-200' : 'text-gray-900'
                    }`}
                  >
                    {t('pages.ticketDetail.attachments')}
                  </h3>
                  {isEditing ? (
                    <div>
                      {ticket.attachments && ticket.attachments.length > 0 && (
                        <div className="mb-4">
                          <h4
                            className={`text-sm font-medium mb-2 ${
                              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                            }`}
                          >
                            {t('pages.ticketDetail.currentAttachments')}
                          </h4>
                          <AttachmentPreview
                            attachments={ticket.attachments}
                            onDelete={handleDeleteAttachment}
                            showDelete={true}
                          />
                        </div>
                      )}
                      <div>
                        <h4
                          className={`text-sm font-medium mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                          }`}
                        >
                          {t('pages.ticketDetail.addNewAttachments')}
                        </h4>
                        <FileUpload
                          onChange={(files) => {
                            setEditedAttachments(files);
                            setUploadProgress({});
                          }}
                          value={editedAttachments}
                          disabled={isSaving}
                        />

                        {/* Upload progress indicators */}
                        {Object.entries(uploadProgress).map(([fileName, progress]) => (
                          <div key={fileName} className="mt-2">
                            <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
                              <span>{fileName}</span>
                              <span>{progress}%</span>
                            </div>
                            <div
                              className={`w-full ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'} rounded-full h-2.5`}
                            >
                              <div
                                className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                                style={{ width: `${progress}%` }}
                              />
                            </div>
                          </div>
                        ))}

                        {/* Preview of new attachments */}
                        {editedAttachments.length > 0 && (
                          <div className="mt-4">
                            <h4
                              className={`text-sm font-medium mb-2 ${
                                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                              }`}
                            >
                              {t('pages.ticketDetail.newAttachmentsPreview')}
                            </h4>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                              {editedAttachments.map((file, index) => (
                                <div
                                  key={`${file.name}-${index}`}
                                  className={`relative group border rounded-lg p-2 ${theme === 'dark' ? 'border-gray-700 hover:bg-gray-800/50' : 'border-gray-200 hover:bg-gray-50'}`}
                                >
                                  {file.type.startsWith('image/') ? (
                                    <div
                                      className={`aspect-square w-full overflow-hidden rounded-md ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'}`}
                                    >
                                      <img
                                        src={URL.createObjectURL(file)}
                                        alt={file.name}
                                        className="h-full w-full object-contain"
                                        onLoad={(e) =>
                                          URL.revokeObjectURL((e.target as HTMLImageElement).src)
                                        }
                                      />
                                    </div>
                                  ) : file.type === 'application/pdf' ||
                                    file.name.toLowerCase().endsWith('.pdf') ? (
                                    <div
                                      className={`aspect-square w-full overflow-hidden rounded-md`}
                                    >
                                      <NewFilePdfThumbnail file={file} theme={theme} />
                                    </div>
                                  ) : file.type === 'application/zip' ||
                                    file.name.toLowerCase().endsWith('.zip') ? (
                                    <div
                                      className={`aspect-square w-full flex items-center justify-center rounded-md ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'}`}
                                    >
                                      <FolderIcon className="h-12 w-12 text-gray-400" />
                                    </div>
                                  ) : (
                                    <div
                                      className={`aspect-square w-full flex items-center justify-center rounded-md ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'}`}
                                    >
                                      <DocumentIcon className="h-12 w-12 text-gray-400" />
                                    </div>
                                  )}
                                  <div className="mt-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}">
                                    <p className="text-sm truncate" title={file.name}>
                                      {file.name}
                                    </p>
                                    <div className="flex items-center justify-between">
                                      <p className="text-xs text-gray-500">
                                        {formatFileSize(file.size)}
                                      </p>
                                      {(() => {
                                        // Determine file type for badge
                                        let fileType = '';
                                        let badgeClass = '';

                                        if (file.type.startsWith('image/')) {
                                          return null; // No badge for images
                                        } else if (
                                          file.type === 'application/pdf' ||
                                          file.name.toLowerCase().endsWith('.pdf')
                                        ) {
                                          fileType = 'PDF';
                                          badgeClass =
                                            'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
                                        } else if (
                                          file.type === 'application/msword' ||
                                          file.type ===
                                            'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                                          file.name.toLowerCase().endsWith('.doc') ||
                                          file.name.toLowerCase().endsWith('.docx')
                                        ) {
                                          fileType = 'DOC';
                                          badgeClass =
                                            'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
                                        } else if (
                                          file.type === 'application/vnd.ms-excel' ||
                                          file.type ===
                                            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                                          file.name.toLowerCase().endsWith('.xls') ||
                                          file.name.toLowerCase().endsWith('.xlsx')
                                        ) {
                                          fileType = 'XLS';
                                          badgeClass =
                                            'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
                                        } else if (
                                          file.type === 'text/csv' ||
                                          file.name.toLowerCase().endsWith('.csv')
                                        ) {
                                          fileType = 'CSV';
                                          badgeClass =
                                            'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400';
                                        } else if (
                                          file.type === 'text/plain' ||
                                          file.name.toLowerCase().endsWith('.txt')
                                        ) {
                                          fileType = 'TXT';
                                          badgeClass =
                                            'bg-gray-100 text-gray-800 dark:bg-gray-700/50 dark:text-gray-400';
                                        } else if (
                                          file.type === 'application/zip' ||
                                          file.name.toLowerCase().endsWith('.zip')
                                        ) {
                                          fileType = 'ZIP';
                                          badgeClass =
                                            'bg-gray-100 text-gray-800 dark:bg-gray-700/50 dark:text-gray-400';
                                        } else {
                                          fileType = 'DOC';
                                          badgeClass =
                                            'bg-gray-100 text-gray-800 dark:bg-gray-700/50 dark:text-gray-400';
                                        }

                                        return (
                                          <span
                                            className={`text-xs px-1.5 py-0.5 rounded-sm ${badgeClass}`}
                                          >
                                            {fileType}
                                          </span>
                                        );
                                      })()}
                                    </div>
                                  </div>

                                  {/* Remove button */}
                                  <button
                                    onClick={() => {
                                      const newFiles = [...editedAttachments];
                                      newFiles.splice(index, 1);
                                      setEditedAttachments(newFiles);
                                    }}
                                    className={`absolute top-2 right-2 p-1 rounded-md ${theme === 'dark' ? 'bg-gray-800/90 hover:bg-red-900/50 text-red-400 hover:text-red-300' : 'bg-white/90 hover:bg-red-50 text-red-500 hover:text-red-700'}`}
                                    title="Remove"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <>
                      {ticket.attachments && ticket.attachments.length > 0 ? (
                        <AttachmentPreview
                          attachments={ticket.attachments}
                          onDelete={handleDeleteAttachment}
                          showDelete={false}
                        />
                      ) : (
                        <p
                          className={
                            theme === 'dark' ? 'text-sm text-gray-400' : 'text-sm text-gray-500'
                          }
                        >
                          {t('pages.ticketDetail.noAttachments')}
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Comments Section */}
            <div
              className={`mt-6 ${
                theme === 'dark' ? 'bg-gray-800' : 'bg-white'
              } shadow-sm rounded-xl`}
            >
              <div className="p-6">
                <h3
                  className={`text-lg font-medium mb-4 ${
                    theme === 'dark' ? 'text-gray-200' : 'text-gray-900'
                  }`}
                >
                  {t('pages.ticketDetail.discussion')}
                </h3>
                <TicketCommentForm
                  onCommentSubmit={handleAddComment}
                  isSubmitting={isLoadingComments}
                />
                <div className="mt-6">
                  <TicketCommentList
                    comments={comments}
                    loggedInUserId={user?.id || 0}
                    onDeleteAttachment={handleDeleteCommentAttachment}
                    onDeleteComment={handleDeleteComment}
                    onEditComment={handleEditComment}
                    ticketId={id}
                  />

                  {/* Inline comment editing is now handled in the TicketCommentList component */}
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel - Metadata */}
          <div className="lg:col-span-1">
            <div
              className={`${
                theme === 'dark' ? 'bg-gray-800' : 'bg-white'
              } shadow-sm rounded-xl p-6`}
            >
              <h3
                className={`text-lg font-medium mb-4 ${
                  theme === 'dark' ? 'text-gray-200' : 'text-gray-900'
                }`}
              >
                {t('pages.ticketDetail.ticketDetail')}
              </h3>
              <dl className="space-y-4">
                <div>
                  <dt
                    className={
                      theme === 'dark'
                        ? 'text-sm font-medium text-gray-400'
                        : 'text-sm font-medium text-gray-500'
                    }
                  >
                    {t('pages.ticketDetail.requester')}
                  </dt>
                  <dd className="mt-1 text-sm">
                    <div className="flex items-center">
                      {ticket.created_by ? (
                        <>
                          <div
                            className={`h-8 w-8 rounded-full relative overflow-hidden mr-2 ${
                              theme === 'dark' ? 'ring-1 ring-blue-400/50' : ''
                            } ${!ticket.created_by?.pfp_url ? 'flex items-center justify-center bg-blue-100 text-blue-600' : ''}`}
                          >
                            {/* Profile picture with fallback to initials */}
                            {ticket.created_by?.pfp_url ? (
                              <img
                                src={formatProfilePictureUrl(ticket.created_by.pfp_url)}
                                alt={ticket.created_by?.full_name || 'User'}
                                className="h-full w-full object-cover"
                                onError={(e) => {
                                  // Fallback to initials if image fails to load
                                  const imgElement = e.target as HTMLImageElement;
                                  imgElement.style.display = 'none';
                                  const nextElement = imgElement.nextElementSibling as HTMLElement;
                                  if (nextElement) {
                                    nextElement.style.display = 'flex';
                                  }
                                }}
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center">
                                <span className="text-lg font-medium">
                                  {ticket.created_by?.full_name?.[0] || 'U'}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="ml-2">
                            {ticket.created_by?.full_name} ({ticket.created_by?.email})
                          </div>
                        </>
                      ) : (
                        <div>{t('pages.ticketDetail.unassigned')}</div>
                      )}
                    </div>
                  </dd>
                </div>

                {/* Customer Details */}
                <div>
                  <dt
                    className={
                      theme === 'dark'
                        ? 'text-sm font-medium text-gray-400'
                        : 'text-sm font-medium text-gray-500'
                    }
                  >
                    {t('pages.ticketDetail.customer')}
                  </dt>
                  <dd
                    className={`mt-1 text-sm ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-900'
                    } flex items-center`}
                  >
                    {isEditing ? (
                      <CustomerSearch
                        onSelectCustomer={(customer) => {
                          setCustomerError('');
                          setEditedCustomer({
                            id: customer.id,
                            full_name: customer.full_name,
                            phone: customer.phone
                          });
                          setEditedPhoneNumber(customer.phone || searchPhoneNumber);
                          setTicket((prev) => {
                            return prev
                              ? {
                                  ...prev,
                                  customer_id: customer.id,
                                  customer_name: customer.full_name,
                                  phone_number: customer.phone || searchPhoneNumber,
                                  customer: {
                                    id: customer.id,
                                    full_name: customer.full_name,
                                    phone: customer.phone || searchPhoneNumber
                                  }
                                }
                              : null;
                          });
                        }}
                        selectedCustomer={editedCustomer}
                        selectedPhone={editedPhoneNumber}
                        onCreateCustomer={(phoneNumber) => {
                          setSearchPhoneNumber(phoneNumber);
                          setCustomerError('');
                          setIsCustomerModalOpen(true);
                        }}
                        searchResults={searchResults}
                        setSearchResults={setSearchResults}
                      />
                    ) : (
                      <>
                        <div
                          className={`h-8 w-8 rounded-full flex items-center justify-center mr-2 ${
                            theme === 'dark'
                              ? 'bg-indigo-900/50 text-indigo-300 ring-1 ring-indigo-400/50'
                              : 'bg-indigo-100 text-indigo-600'
                          }`}
                        >
                          <span className="text-lg font-medium">
                            {ticket.customer?.full_name?.[0]}
                          </span>
                        </div>
                        <div>
                          {ticket.customer?.phone || 'No phone'}
                          <div className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>
                            {ticket.customer?.full_name}
                            {ticket.customer?.deleted_at && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                                Deleted
                              </span>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </dd>
                </div>

                {/* Group */}
                <div>
                  <dt
                    className={
                      theme === 'dark'
                        ? 'text-sm font-medium text-gray-400'
                        : 'text-sm font-medium text-gray-500'
                    }
                  >
                    {t('pages.ticketDetail.group')}
                  </dt>
                  <dd className="mt-1 text-sm">
                    {isEditing ? (
                      <select
                        value={editedGroup}
                        onChange={(e) => {
                          setEditedGroup(e.target.value);
                          setEditedAssignee(''); // Reset assignee when group changes
                        }}
                        className={`mt-1 w-full px-3 py-2 border rounded-md ${
                          theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      >
                        <option value="">Select Group</option>
                        {groups?.map((group) => (
                          <option key={group.value} value={group.value}>
                            {group.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span>{ticket.group_name || 'Unassigned'}</span>
                    )}
                  </dd>
                </div>

                {/* Assignee */}
                <div>
                  <dt
                    className={
                      theme === 'dark'
                        ? 'text-sm font-medium text-gray-400'
                        : 'text-sm font-medium text-gray-500'
                    }
                  >
                    {t('pages.ticketDetail.assignee')}
                  </dt>
                  <dd className="mt-1 text-sm">
                    {isEditing ? (
                      <select
                        value={editedAssignee}
                        onChange={(e) => setEditedAssignee(e.target.value)}
                        disabled={!editedGroup}
                        className={`mt-1 w-full px-3 py-2 border rounded-md ${
                          theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        } ${!editedGroup ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <option value="">Unassigned</option>
                        {groupAgents.map((agent) => (
                          <option key={agent.value} value={agent.value}>
                            {agent.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div>
                        <div className="flex items-center gap-2">
                          {/* We filter with id because unassigned comes with `null` id */}
                          {ticket.assigned_to?.id ? (
                            <>
                              <div
                                className={`h-8 w-8 rounded-full relative overflow-hidden ${
                                  theme === 'dark' ? 'ring-1 ring-blue-400/50' : ''
                                } ${!ticket.assigned_to?.pfp_url ? 'flex items-center justify-center bg-blue-100 text-blue-600' : ''}`}
                              >
                                {/* Profile picture with fallback to initials */}
                                {ticket.assigned_to?.pfp_url ? (
                                  <img
                                    src={formatProfilePictureUrl(ticket.assigned_to.pfp_url)}
                                    alt={ticket.assigned_to?.full_name || 'User'}
                                    className="h-full w-full object-cover"
                                    onError={(e) => {
                                      // Fallback to initials if image fails to load
                                      const imgElement = e.target as HTMLImageElement;
                                      imgElement.style.display = 'none';
                                      const nextElement =
                                        imgElement.nextElementSibling as HTMLElement;
                                      if (nextElement) {
                                        nextElement.style.display = 'flex';
                                      }
                                    }}
                                  />
                                ) : (
                                  <div className="flex h-full w-full items-center justify-center">
                                    <span className="text-lg font-medium">
                                      {ticket.assigned_to?.full_name?.[0] || 'U'}
                                    </span>
                                  </div>
                                )}
                              </div>
                              <div className="ml-2">
                                {ticket.assigned_to?.full_name} ({ticket.assigned_to?.email})
                              </div>
                            </>
                          ) : (
                            <>
                              <div>
                                <span className="block mb-2">
                                  {t('pages.ticketDetail.unassigned')}
                                </span>
                                {userIsInGroup && (
                                  <button
                                    onClick={handlePickupTicket}
                                    className={`px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                                      theme === 'dark'
                                        ? 'bg-blue-600 hover:bg-blue-500 focus:ring-blue-400 focus:ring-offset-gray-800'
                                        : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
                                    }`}
                                    disabled={!userIsInGroup}
                                  >
                                    {t('pages.ticketDetail.assignToMe')}
                                  </button>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </dd>
                </div>

                {/* Ticket Attributes */}
                <div className="grid grid-cols-3 gap-4">
                  {/* Type */}
                  <div>
                    <dt
                      className={
                        theme === 'dark'
                          ? 'text-sm font-medium text-gray-300'
                          : 'text-sm font-medium text-gray-500'
                      }
                    >
                      {t('pages.ticketDetail.type')}
                    </dt>
                    <dd className="mt-1 text-sm">
                      {isEditing ? (
                        <select
                          value={editedType}
                          onChange={(e) => setEditedType(e.target.value)}
                          className={`w-full px-2 py-1 text-xs font-medium border rounded-full ${
                            theme === 'dark'
                              ? '[&>option]:bg-gray-800 [&>option]:text-blue-200 bg-blue-900/30 border-blue-500/50 text-blue-400 hover:bg-blue-900/40 focus:border-blue-400'
                              : 'bg-blue-100 border-blue-200 text-blue-800'
                          }`}
                        >
                          <option value="" className={theme === 'dark' ? 'bg-gray-800' : ''}>
                            {t('pages.ticketDetail.selectType')}
                          </option>
                          {types?.map((type) => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span
                          className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${
                            theme === 'dark'
                              ? 'bg-blue-900/30 text-blue-400'
                              : 'bg-blue-100 text-blue-800'
                          }`}
                        >
                          {ticket.type_name}
                        </span>
                      )}
                    </dd>
                  </div>

                  {/* Category */}
                  <div>
                    <dt
                      className={
                        theme === 'dark'
                          ? 'text-sm font-medium text-gray-300'
                          : 'text-sm font-medium text-gray-500'
                      }
                    >
                      {t('pages.ticketDetail.category')}
                    </dt>
                    <dd className="mt-1 text-sm">
                      {isEditing ? (
                        <select
                          value={editedCategory}
                          onChange={(e) => setEditedCategory(e.target.value)}
                          className={`w-full px-2 py-1 text-xs font-medium border rounded-full ${
                            theme === 'dark'
                              ? '[&>option]:bg-gray-800 [&>option]:text-green-200 bg-green-900/30 border-green-500/50 text-green-400 hover:bg-green-900/40 focus:border-green-400'
                              : 'bg-green-100 border-green-200 text-green-800'
                          }`}
                        >
                          <option value="" className={theme === 'dark' ? 'bg-gray-800' : ''}>
                            {t('pages.ticketDetail.selectCategory')}
                          </option>
                          {categories?.map((category) => (
                            <option key={category.value} value={category.value}>
                              {category.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span
                          className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${
                            theme === 'dark'
                              ? 'bg-green-900/30 text-green-400'
                              : 'bg-green-100 text-green-800'
                          }`}
                        >
                          {ticket.category_name}
                        </span>
                      )}
                    </dd>
                  </div>

                  {/* Source */}
                  <div>
                    <dt
                      className={
                        theme === 'dark'
                          ? 'text-sm font-medium text-gray-300'
                          : 'text-sm font-medium text-gray-500'
                      }
                    >
                      {t('pages.ticketDetail.source')}
                    </dt>
                    <dd className="mt-1 text-sm">
                      {isEditing ? (
                        <select
                          value={editedSource}
                          onChange={(e) => setEditedSource(e.target.value)}
                          className={`w-full px-2 py-1 text-xs font-medium border rounded-full ${
                            theme === 'dark'
                              ? '[&>option]:bg-gray-800 [&>option]:text-red-200 bg-red-900/30 border-red-500/50 text-red-400 hover:bg-red-900/40 focus:border-red-400'
                              : 'bg-red-100 border-red-200 text-red-800'
                          }`}
                        >
                          <option value="" className={theme === 'dark' ? 'bg-gray-800' : ''}>
                            Select Source
                          </option>
                          {sources?.map((source) => (
                            <option key={source.value} value={source.value}>
                              {source.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span
                          className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${
                            theme === 'dark'
                              ? 'bg-red-900/30 text-red-400'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {ticket.source_name}
                        </span>
                      )}
                    </dd>
                  </div>
                </div>

                {/* Tags */}
                <div>
                  <dt
                    className={
                      theme === 'dark'
                        ? 'text-sm font-medium text-gray-400'
                        : 'text-sm font-medium text-gray-500'
                    }
                  >
                    {t('pages.ticketDetail.tags')}
                  </dt>
                  <dd className="mt-1 text-sm">
                    {isEditing ? (
                      <AsyncSelect
                        isMulti
                        value={editedTags}
                        onChange={handleTagChange}
                        loadOptions={loadTagOptions}
                        defaultOptions
                        placeholder="Search or select tags..."
                        noOptionsMessage={({ inputValue }) =>
                          inputValue ? 'No matching tags found' : 'Type to search tags'
                        }
                        className={theme === 'dark' ? 'react-select-dark' : ''}
                        classNamePrefix="react-select"
                        styles={{
                          control: (base) => ({
                            ...base,
                            backgroundColor: theme === 'dark' ? '#1f2937' : base.backgroundColor,
                            borderColor: theme === 'dark' ? '#374151' : base.borderColor
                          }),
                          menu: (base) => ({
                            ...base,
                            backgroundColor: theme === 'dark' ? '#1f2937' : base.backgroundColor
                          }),
                          option: (base, state) => ({
                            ...base,
                            backgroundColor:
                              theme === 'dark'
                                ? state.isFocused
                                  ? '#374151'
                                  : '#1f2937'
                                : state.isFocused
                                  ? '#f3f4f6'
                                  : base.backgroundColor,
                            color: theme === 'dark' ? '#e5e7eb' : base.color
                          }),
                          multiValue: (base) => ({
                            ...base,
                            backgroundColor:
                              theme === 'dark' ? 'rgba(147, 51, 234, 0.3)' : 'rgb(243, 232, 255)'
                          }),
                          multiValueLabel: (base) => ({
                            ...base,
                            color: theme === 'dark' ? 'rgb(192, 132, 252)' : 'rgb(107, 33, 168)'
                          }),
                          multiValueRemove: (base) => ({
                            ...base,
                            color: theme === 'dark' ? 'rgb(192, 132, 252)' : 'rgb(107, 33, 168)',
                            ':hover': {
                              backgroundColor:
                                theme === 'dark' ? 'rgba(147, 51, 234, 0.5)' : 'rgb(233, 213, 255)',
                              color: theme === 'dark' ? 'rgb(216, 180, 254)' : 'rgb(88, 28, 135)'
                            }
                          })
                        }}
                      />
                    ) : (
                      ticket?.tags?.map((tag) => (
                        <span
                          key={tag.id}
                          className={`inline-flex items-center px-2 py-0.5 mr-2 text-xs font-medium rounded-full ${
                            theme === 'dark'
                              ? 'bg-purple-900/30 text-purple-400'
                              : 'bg-purple-100 text-purple-800'
                          }`}
                        >
                          {tag.name}
                        </span>
                      ))
                    )}
                  </dd>
                </div>

                <TicketDates createdAt={ticket.created_at} updatedAt={ticket.updated_at} />

                {canDelete && (
                  <div className="mt-6">
                    <button
                      onClick={() => setIsDeleteModalOpen(true)}
                      className={`inline-flex items-center px-4 py-2 border rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${
                        theme === 'dark'
                          ? 'border-red-500/50 text-red-400 bg-red-900/20 hover:bg-red-900/30'
                          : 'border-red-300 text-red-700 bg-white hover:bg-red-50'
                      }`}
                    >
                      <TrashIcon
                        className={`h-5 w-5 mr-2 ${
                          theme === 'dark' ? 'text-red-400' : 'text-red-500'
                        }`}
                      />
                      {t('pages.ticketDetail.deleteTicket')}
                    </button>
                  </div>
                )}
              </dl>
            </div>
            {/* Ticket History Section */}
            <TicketHistory
              auditLogs={auditLogs}
              isLoadingAuditLogs={isLoadingAuditLogs}
              fetchAuditLogs={fetchAuditLogs}
            />
          </div>
        </div>
      </Container>

      <Dialog open={isDeleteModalOpen} onOpenChange={(open) => setIsDeleteModalOpen(open)}>
        <DialogContent>
          <DialogTitle className="flex items-center gap-2">
            <ExclamationTriangleIcon className="w-6 h-6 text-red-500" />
            Delete Ticket
          </DialogTitle>
          <div className="py-3">
            <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}>
              Are you sure you want to delete this ticket? This action cannot be undone.
            </p>
          </div>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setIsDeleteModalOpen(false)}
              className={`px-4 py-2 text-sm font-medium rounded-md ${
                theme === 'dark'
                  ? 'text-gray-300 bg-gray-700 hover:bg-gray-600'
                  : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
              }`}
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md"
            >
              Delete
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isCustomerModalOpen} onOpenChange={(open) => setIsCustomerModalOpen(open)}>
        <DialogContent className={theme === 'dark' ? 'bg-gray-800 border-gray-700' : undefined}>
          <div className="flex items-center justify-between pb-3 border-b border-gray-200 dark:border-gray-700">
            <DialogTitle
              className={`text-lg font-medium ${
                theme === 'dark' ? 'text-gray-200' : 'text-gray-900'
              }`}
            >
              Create New Customer
            </DialogTitle>
          </div>

          <div className="py-4 space-y-4">
            <div>
              <label
                className={`block text-sm font-medium ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}
              >
                Full Name
              </label>
              <div className="relative">
                <input
                  ref={customerNameInputRef}
                  type="text"
                  value={newCustomerName}
                  onChange={(e) => setNewCustomerName(e.target.value)}
                  className={`mt-1 block w-full rounded-md shadow-sm px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white focus:ring-indigo-500 focus:ring-offset-gray-800'
                      : 'border-gray-300 text-gray-900 focus:ring-indigo-500'
                  }`}
                  placeholder="Enter customer name"
                />
                {externalRefConfig?.enabled && searchPhoneNumber && (
                  <button
                    type="button"
                    onClick={handleExternalReferenceSearch}
                    disabled={isLoadingExternalRef}
                    className={`absolute inset-y-0 right-0 flex items-center pr-3 ${
                      isLoadingExternalRef ? 'cursor-not-allowed' : 'cursor-pointer'
                    }`}
                    title={t('tickets.externalReference.searchTitle', 'Search external reference')}
                  >
                    {isLoadingExternalRef ? (
                      <ArrowPathIcon className="w-5 h-5 text-gray-400 animate-spin" />
                    ) : (
                      <MagnifyingGlassIcon
                        className={`w-5 h-5 ${
                          theme === 'dark'
                            ? 'text-gray-400 hover:text-gray-300'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                      />
                    )}
                  </button>
                )}
              </div>
            </div>

            <div>
              <label
                className={`block text-sm font-medium ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}
              >
                Phone Number
              </label>
              <input
                type="text"
                value={searchPhoneNumber}
                disabled
                className={`mt-1 block w-full rounded-md shadow-sm px-3 py-2 ${
                  theme === 'dark'
                    ? 'bg-gray-600 border-gray-600 text-gray-400'
                    : 'bg-gray-100 border-gray-300 text-gray-500'
                }`}
              />
            </div>
          </div>

          <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setIsCustomerModalOpen(false)}
                className={`px-4 py-2 text-sm font-medium rounded-md border ${
                  theme === 'dark'
                    ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateCustomer}
                disabled={isCreatingCustomer || !newCustomerName}
                className={`px-4 py-2 text-sm font-medium rounded-md ${
                  theme === 'dark'
                    ? 'bg-indigo-600 text-white hover:bg-indigo-500 disabled:bg-gray-700 disabled:text-gray-500'
                    : 'bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-gray-100 disabled:text-gray-500'
                }`}
              >
                {isCreatingCustomer ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

export default TicketDetailPage;
